import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  CheckCircle2,
  AlertCircle,
  Copy,
  RefreshCw,
  Info,
  ScanBarcode,
  ShieldCheck,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { validateGtin, gtinKindDescription, computeCheckDigit } from "@/lib/gtin";
import { usePrefillValue } from "@/lib/usePrefillValue";

const PATH = "/gtin-validator";
const TITLE = "GTIN Validator — Check GTIN-8 / 12 / 13 / 14 Online";
const DESC =
  "Free GTIN validator. Verify the check digit on GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) and GTIN-14 (ITF-14) product identifiers — all in your browser.";

const FAQ = [
  {
    q: "What Is a GTIN?",
    a: "GTIN (Global Trade Item Number) is the GS1 standard product identifier. It comes in four lengths: GTIN-8 (EAN-8), GTIN-12 (UPC-A), GTIN-13 (EAN-13) and GTIN-14 (ITF-14 / logistics). The validator checks all four with the same mod-10 algorithm.",
  },
  {
    q: "How does GTIN check digit validation work?",
    a: "Multiply digits at odd positions counted from the right (excluding the check digit) by 3 and even positions by 1. Sum the results. The check digit is whatever value makes the total a multiple of 10. ReadBarcode runs this check locally and shows the expected digit if yours doesn't match.",
  },
  {
    q: "Why Is My GTIN Invalid?",
    a: "The most common causes are a mistyped digit, an extra or missing digit, or leading/trailing characters from a spreadsheet (apostrophes, spaces). The error message shows the expected check digit, which usually pinpoints the typo.",
  },
  {
    q: "Can I Paste a UPC or EAN Here?",
    a: "Yes — a UPC-A is a GTIN-12 and an EAN-13 is a GTIN-13. They're all the same family. For full structure breakdowns and product lookup links, use the dedicated UPC Lookup or EAN Lookup tools.",
  },
  {
    q: "Does the Validator Look up Product Names?",
    a: "No — this is a pure validate-product-identifier tool. It confirms a GTIN is structurally correct. Product names depend on public databases and are handled by the lookup tools.",
  },
  {
    q: "Is It Safe to Validate GTINs From Confidential Product Sheets?",
    a: "Yes. The check digit math runs entirely in your browser — nothing is sent to a server, logged or stored.",
  },
  {
    q: "Can I Check a GTIN Without the Check Digit?",
    a: "Not here: the validator expects all 8, 12, 13 or 14 digits and reads the last one as the check digit. If you only have the first 7, 11, 12 or 13 digits, the Check Digit Calculator works out the missing one. Paste the completed code back here to confirm it.",
  },
  {
    q: "Does a Valid GTIN Mean It Is Registered?",
    a: "No. A matching check digit only shows that the number is well formed, and anyone can type a structurally valid GTIN that no company has been assigned. To see whether a GTIN is licensed to a brand owner, look it up on Verified by GS1.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: "/lookup-validators" },
  { name: "GTIN Validator", path: PATH },
];

export const Route = createFileRoute("/gtin-validator")({
  component: GtinValidatorPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode GTIN Validator",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function GtinValidatorPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="GTIN Validator"
      description="Validate GTIN-8, GTIN-12, GTIN-13 and GTIN-14 product identifiers. Verifies the check digit locally — no signup, no upload. When the check digit is wrong, the result shows the digit the code should end with and a button that swaps it in, after spaces, dashes, dots and underscores are stripped."
      crumbs={CRUMBS}
      content={
        <>
          <CheckDigitExample />
          <ToolSeoSections
            aboutHeading="About this GTIN validator"
            about={
              <>
                <p>
                  This GTIN validator confirms whether a product identifier is structurally correct
                  using the GS1 mod-10 check digit algorithm. It accepts all four GTIN lengths — GTIN-8,
                  GTIN-12 (UPC-A), GTIN-13 (EAN-13) and GTIN-14 (ITF-14) — so you can paste any retail
                  or logistics code without thinking about the format up front.
                </p>
                <p>
                  The check runs entirely in your browser. There's no upload step, no signup and no paid
                  API behind it.
                </p>
              </>
            }
            whenToUse={[
              "Bulk-checking GTINs before sending a product feed to Amazon, eBay or Shopify.",
              "Catching mistyped digits in a supplier's spreadsheet before importing it.",
              "Verifying a GTIN-14 carton code matches the inner GTIN-13 item code (after wrapping).",
              "Confirming a freshly minted GTIN before issuing it to a printer.",
              "Debugging why a marketplace listing is being suppressed for an invalid identifier.",
            ]}
            howItWorks={[
              {
                title: "Paste the GTIN",
                desc: "Type or paste any GTIN-8/12/13/14. Spaces, dashes, underscores and dots are cleaned automatically.",
              },
              {
                title: "Auto-detect the type",
                desc: "Length determines whether the code is treated as GTIN-8, GTIN-12, GTIN-13 or GTIN-14.",
              },
              {
                title: "Check the check digit",
                desc: "We run the GS1 mod-10 algorithm and compare against the digit you provided.",
              },
              {
                title: "See the result",
                desc: "Valid codes show the detected type and cleaned number. Invalid codes show the expected check digit.",
              },
              {
                title: "Copy or fix",
                desc: "Copy the cleaned, validated code to the clipboard or apply the suggested fix.",
              },
            ]}
            whatToAvoid={[
              "Validating GTINs that still have spreadsheet apostrophes or thousand-separators attached.",
              "Assuming a structurally valid GTIN means the product exists — it only means the number is well-formed.",
              "Treating GTIN-14 cartons and inner GTIN-13 items as interchangeable in feeds.",
              "Using internal SKU numbers as GTINs — they almost never validate.",
              "Trusting any code where someone has 'fixed' the check digit by hand without recomputing it.",
            ]}
            tips={[
              "If validation fails with a one-off check digit, you almost certainly have a typo in the body.",
              "Use the Check Digit Calculator when you have the first 7/11/12/13 digits and need the missing one.",
              "For bulk lists, paste each GTIN in turn — batch tooling is on the roadmap.",
              "A GTIN-13 starting with 0 is the same code as a GTIN-12 (UPC-A) — both are valid.",
              "GTIN-14 'indicator digits' 1–8 mean inner packs/cases; 9 means variable measure.",
            ]}
          />
          <GtinSections />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <GtinApp />
      <SupportedTypes />
    </ToolLayout>
  );
}

type Result = ReturnType<typeof validateGtin> | { ok: "idle" };

function GtinApp() {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  usePrefillValue((v) => {
    setValue(v);
    setSubmitted(true);
  });

  const result: Result = useMemo(() => {
    if (!submitted) return { ok: "idle" } as const;
    return validateGtin(value);
  }, [submitted, value]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const reset = () => {
    setValue("");
    setSubmitted(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <label htmlFor="gtin-input" className="text-sm font-medium">
              GTIN
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="gtin-input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="e.g. 00614141999996"
                maxLength={24}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSubmitted(false);
                }}
                aria-invalid={"ok" in result && result.ok === false}
              />
              <Button type="submit" disabled={!value.trim()}>
                <ShieldCheck className="mr-2 h-4 w-4" /> Validate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Accepts GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) or GTIN-14 (ITF-14). Spaces, dashes
              and dots are ignored.
            </p>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    setValue(ex);
                    setSubmitted(true);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                >
                  {ex}
                </button>
              ))}
            </div>
            <div>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ResultPanel
        result={result}
        onUseSuggested={(s) => {
          setValue(s);
          setSubmitted(true);
        }}
      />
    </div>
  );
}

const EXAMPLES = ["73513537", "036000291452", "5012345678900", "10614141000415"];

function ResultPanel({
  result,
  onUseSuggested,
}: {
  result: Result;
  onUseSuggested: (s: string) => void;
}) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Result
        </p>
        <div className="mt-4 flex-1">
          {"ok" in result && result.ok === "idle" && (
            <EmptyState>
              Paste a GTIN and press Validate. The detected type, cleaned number and check digit
              will appear here.
            </EmptyState>
          )}

          {"ok" in result && result.ok === false && (
            <InvalidView result={result} onUseSuggested={onUseSuggested} />
          )}

          {"ok" in result && result.ok === true && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Valid {result.kind}</span>
                <Badge variant="outline" className="text-xs">
                  Check digit {result.checkDigit}
                </Badge>
              </div>
              <Row label="Detected type" value={result.kind} />
              <Row label="Cleaned number" value={result.normalized} mono onCopy={copy} />
              <p className="rounded-md border border-border bg-background p-3 text-xs text-muted-foreground">
                <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
                {gtinKindDescription(result.kind)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function InvalidView({
  result,
  onUseSuggested,
}: {
  result: Extract<Result, { ok: false }>;
  onUseSuggested: (s: string) => void;
}) {
  // Build a suggested fix when only the check digit is wrong.
  let suggested: string | null = null;
  if (result.kind && result.expected !== undefined && result.cleaned.length >= 8) {
    suggested = `${result.cleaned.slice(0, -1)}${result.expected}`;
    // Sanity check.
    const recomputed = computeCheckDigit(suggested.slice(0, -1));
    if (recomputed !== Number(suggested[suggested.length - 1])) suggested = null;
  }
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-center gap-2 text-destructive">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm font-medium">Invalid GTIN</span>
      </div>
      <p className="text-sm text-muted-foreground">{result.reason}</p>
      {result.cleaned && <Row label="Cleaned input" value={result.cleaned} mono />}
      {suggested && (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-background p-3">
          <p className="text-sm">
            <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
            If your first {suggested.length - 1} digits are correct, the {result.kind} should be{" "}
            <span className="font-mono">{suggested}</span>.
          </p>
          <div>
            <Button size="sm" variant="outline" onClick={() => onUseSuggested(suggested!)}>
              Use suggested check digit
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy?: (s: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-0.5 break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      {onCopy && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCopy(value)}
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <ScanBarcode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function CheckDigitExample() {
  // Worked mod-10 example for GTIN-12 03600029145 (check digit 2).
  // Weights alternate ×3/×1 anchored at the RIGHTMOST body digit, so for an
  // 11-digit body the rightmost digit (5) is ×3. Matches computeCheckDigit().
  const steps = [
    { label: "Take the GTIN without its check digit", value: "0 3 6 0 0 0 2 9 1 4 5" },
    {
      label: "Multiply digits alternately by 3 and 1 (from the right)",
      value: "0×3 3×1 6×3 0×1 0×3 0×1 2×3 9×1 1×3 4×1 5×3",
    },
    { label: "Sum the products", value: "0 + 3 + 18 + 0 + 0 + 0 + 6 + 9 + 3 + 4 + 15 = 58" },
    { label: "Round up to the next multiple of 10", value: "60" },
    { label: "Subtract the sum — that's the check digit", value: "60 − 58 = 2" },
  ];
  return (
    <>
      <h2>How the GTIN check digit is calculated</h2>
      <p>
        Every GTIN ends in a mod-10 check digit that catches every single-digit typo and most
        transpositions. The validator runs exactly these steps for you — here is the math worked out
        by hand so you can verify a code anywhere.
      </p>
      <ol>
        {steps.map((s, i) => (
          <li key={i}>
            <strong>{s.label}</strong>{" "}<code>{s.value}</code>
          </li>
        ))}
      </ol>
      <p>
        The same algorithm applies to GTIN-8, GTIN-12, GTIN-13 and GTIN-14 — only the number of
        digits changes.
      </p>
    </>
  );
}

function SupportedTypes() {
  const types = [
    { kind: "GTIN-8", desc: "8-digit EAN-8 for small retail packages." },
    { kind: "GTIN-12", desc: "12-digit UPC-A used in the US and Canada." },
    { kind: "GTIN-13", desc: "13-digit EAN-13 — the global retail standard." },
    { kind: "GTIN-14", desc: "14-digit ITF-14 used on cases, cartons and pallets." },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Supported GTIN Types</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        The validator handles every standard GS1 GTIN length with the same mod-10 algorithm.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {types.map((t) => (
          <div key={t.kind} className="rounded-lg border border-border bg-background p-4">
            <div className="text-sm font-semibold">{t.kind}</div>
            <p className="mt-1 text-xs text-muted-foreground">{t.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
function GtinSections() {
  return (
    <>
      <h2>GTIN Checker: Is This GTIN Valid?</h2>
      <p>A GTIN is valid when it has 8, 12, 13 or 14 digits and ends in the correct <Link to="/barcode-guides/check-digits-explained">mod-10 check digit</Link>.</p>
      <h3>GS1 GTIN Check vs This Validator</h3>
      <p>Verified by GS1 shows whether a GTIN is registered to a brand owner. This validator is a GTIN check of the number only, so run it first.</p>
      <h2>GTIN-13 and GTIN-14 Validation</h2>
      <p>Both use the same calculation; the validator tells them apart by length. See the <Link to="/barcode-guides/what-is-a-gtin">GTIN guide</Link> for where each is used.</p>
      <h3>Validating a GTIN-14 Case Code</h3>
      <p>A GTIN-14 is often an indicator digit, the item GTIN-13 without its check digit, and a new check digit. Example 10614141000415 wraps item 0614141000418.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/barcode-guides/what-is-a-gtin",
      label: "What Is a GTIN?",
      desc: "Guide to the four GTIN lengths and where each one is used.",
    },
    {
      path: "/barcode-guides/check-digits-explained",
      label: "Check Digits Explained",
      desc: "How mod-10 and other check digits catch typing errors.",
    },
    {
      path: "/upc-lookup",
      label: "UPC Lookup",
      desc: "Validate UPC-A / UPC-E and decode the number system digit.",
    },
    {
      path: "/ean-lookup",
      label: "EAN Lookup",
      desc: "Validate EAN-13 / EAN-8 and decode the GS1 country prefix.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the GTIN check digit when you only have the body.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan a GTIN with your camera or upload an image.",
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Decode a GTIN from a photo or screenshot.",
    },
    {
      path: "/gs1-digital-link-decoder",
      label: "GS1 Digital Link Decoder",
      desc: "Parse Digital Link URLs containing a GTIN, lot and serial.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
